import { FORM_ERROR } from "final-form"
import { useCallback, useEffect } from "react"
import { Field, Form } from "react-final-form"

import { consultationClient, useMutation } from "@trueskin-web/apis"
import { Dialog, Heading } from "@trueskin-web/components"
import { Button, FieldError, TextareaControl } from "@trueskin-web/components"
import {
  composeValidators,
  getErrorMessage,
  maxLength,
  required,
} from "@trueskin-web/functions"

import useFocus from "../../hooks/use-focus"

const UserNoteDialog = ({ isOpen, userNote = "", onDismiss, onSubmit }) => {
  const [focusRef, setFocusRef] = useFocus()

  const { mutateAsync: sendNoteForDoctor } = useMutation(
    consultationClient.sendNoteToDoctor,
    {
      onSuccess: onSubmit,
    }
  )

  const handleSubmit = useCallback(
    async ({ content }) => {
      try {
        await sendNoteForDoctor({ content })
      } catch (err) {
        return { [FORM_ERROR]: getErrorMessage(err) }
      }
    },
    [sendNoteForDoctor, onDismiss]
  )

  useEffect(() => {
    const timerId = setTimeout(() => {
      setFocusRef()
    }, 700)

    return () => clearTimeout(timerId)
  }, [focusRef, setFocusRef])

  return (
    <Dialog
      rounded
      noMobileFullscreen
      isOpen={isOpen}
      onDismiss={onDismiss}
      dataTestId="user-note-dialog"
      sx={{
        mt: "62px",
        "&[data-reach-dialog-content]": {
          borderRadius: "16px",
        },
      }}
    >
      <Heading sx={{ mb: 2, fontWeight: "bold !important" }}>
        Gibt es Bedenken, Wünsche oder frühere Behandlungen?
      </Heading>

      <div sx={{ mb: 4, fontSize: 1, color: "neutralsDarkGrey" }}>
        Gib Details zu Deinen Problemen oder Produkt-Vorlieben an. Diese werden
        Deinem medizinischen Fall hinzugefügt und vom Deinem Arzt/Deiner Ärztin
        geprüft.
      </div>

      <Form
        onSubmit={handleSubmit}
        initialValues={{
          content: userNote,
        }}
        render={({
          values,
          valid,
          isSubmitting,
          submitError,
          handleSubmit,
        }) => {
          return (
            <>
              <Field
                ref={focusRef}
                name="content"
                component={TextareaControl}
                rows={4}
                placeholder="Gib hier Deine Antwort ein..."
                type="text"
                size="lg"
                validate={composeValidators(
                  (v) => required(v, "Bitte gib eine Antwort ein."),
                  maxLength(600)
                )}
                data-testid="user-note-dialog-textarea"
                sx={{
                  textarea: {
                    p: 4,
                    borderRadius: "8px",
                    "&, &:hover, &:focus, &:active": {
                      border: "1px",
                      borderColor: "neutralsMediumGrey",
                      boxShadow: "none",
                    },
                    "&[aria-invalid=true]": {
                      borderColor: "attention",
                      boxShadow: "none",
                    },
                  },
                }}
              />

              {submitError && (
                <FieldError sx={{ mb: 0 }}>{submitError}</FieldError>
              )}

              <div
                sx={{
                  mt: 1,
                  fontSize: 0,
                  lineHeight: "body",
                  color: "neutralsDarkGrey",
                }}
                data-testid="user-note-dialog-count"
              >
                {`${values.content?.length ?? 0}/600`}
              </div>

              <Button
                type="submit"
                isFullWidth
                rounded
                size="lg"
                onClick={handleSubmit}
                isDisabled={!valid}
                isLoading={isSubmitting}
                data-testid="user-note-dialog-submit-button"
                sx={{ mt: 6 }}
              >
                Absenden
              </Button>
            </>
          )
        }}
      />
    </Dialog>
  )
}

export default UserNoteDialog
